import {
  EntityTypeForRoleManagement,
  EntityTypes,
  Permission,
  RoleManagementScope
} from '../../types/role'

// IAM decides who may manage which role and enforces it on every write. These helpers only
// read its answer, so the screens offer what the backend would accept instead of letting
// the user find out from an error.

// An unknown answer (null/undefined: an older backend) keeps the actions available rather
// than hiding them from everyone, admins included.
export const canManageRole = (canManage?: boolean | null): boolean =>
  canManage !== false

// The entity types to offer when creating a role. Without a scope (still loading, or an
// older backend) the full list stays, as before.
export const creatableEntityTypes = (
  scope?: RoleManagementScope | null
): { label: string; value: EntityTypeForRoleManagement }[] => {
  if (!scope || scope.unrestricted) return EntityTypes
  return EntityTypes.filter((entry) =>
    scope.creatableEntityTypes.includes(entry.value)
  )
}

// Whether the user may create any role at all.
export const mayCreateRoles = (scope?: RoleManagementScope | null): boolean =>
  !scope || scope.unrestricted || scope.creatableEntityTypes.length > 0

// Greys out every action the user may not grant. A permission the role already carries
// stays usable: keeping or re-adding it grants nothing new, and IAM allows it.
export const applyGrantableScope = (
  permissions: Permission[],
  scope: RoleManagementScope | null | undefined,
  alreadyOnRole: Set<string>
): Permission[] => {
  if (!scope || scope.unrestricted) return permissions
  const grantable = new Set(scope.grantablePermissionIDs)
  return permissions.map((permission) => ({
    ...permission,
    subPermissions: permission.subPermissions.map((sub) => ({
      ...sub,
      actions: sub.actions.map((action) => ({
        ...action,
        disabled:
          action.disabled ||
          (!grantable.has(action.permissionID) &&
            !alreadyOnRole.has(action.permissionID))
      }))
    }))
  }))
}

// Every action read-only, for a role the user may view but not change.
export const readOnlyPermissions = (permissions: Permission[]): Permission[] =>
  permissions.map((permission) => ({
    ...permission,
    subPermissions: permission.subPermissions.map((sub) => ({
      ...sub,
      actions: sub.actions.map((action) => ({ ...action, disabled: true }))
    }))
  }))

// The ids of the permissions currently selected on a role.
export const selectedPermissionIDs = (permissions: Permission[]): Set<string> =>
  new Set(
    permissions
      .flatMap((permission) => permission.subPermissions)
      .flatMap((sub) => sub.actions)
      .filter((action) => action.selected)
      .map((action) => action.permissionID)
  )
